import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { applyTheme, getSelectedTheme } from './themes';
import { ThemeSelector } from './ThemeSelector';
import { SurveyList } from './components/SurveyList';
import MobilePreview from './components/MobilePreview';

// Use environment variable for API URL, fallback to relative path for local dev
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

interface QuestionOption {
  id: string;
  text: string | { id: string; text: string; value: any };
  value: any;  // Backend sends value, not order
}

interface Question {
  id: string;
  type: string;
  title: string;
  description?: string;
  required: boolean;
  order: number;
  options?: QuestionOption[];
  placeholder?: string;
  followUp?: {
    triggerOption: string;
    questionText: string;
    type: 'text' | 'textarea';
  };
}

interface Survey {
  id: string;
  title: string;
  description?: string;
  closing_message?: string;
  is_published: boolean;
  slug: string;  // Changed from unique_url_slug to match backend response
  unique_url_slug?: string;  // Some surveys may still use this field
  questions: Question[];
}

interface AnswerValue {
  question_id: string;
  value: any;
}

function App() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [survey, setSurvey] = useState<Survey | null>(null);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [visibleFollowUps, setVisibleFollowUps] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [docuSignUrl, setDocuSignUrl] = useState<string | null>(null);
  const [countdown, setCountdown] = useState<number>(5);
  const [currentTheme, setCurrentTheme] = useState(getSelectedTheme());

  // Apply theme on mount and when changed
  useEffect(() => {
    applyTheme(currentTheme);
  }, [currentTheme]);

  // Handle countdown and redirect to DocuSign
  useEffect(() => {
    if (submitted && docuSignUrl && countdown > 0) {
      console.log(`Countdown: ${countdown} seconds remaining`);
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (submitted && docuSignUrl && countdown === 0) {
      console.log('Countdown reached 0, attempting redirect to DocuSign:', docuSignUrl);
      try {
        window.location.href = docuSignUrl;
        console.log('Redirect initiated successfully');
      } catch (error) {
        console.error('Redirect failed:', error);
        alert('Automatic redirect failed. Please click the button to continue to DocuSign.');
      }
    }
  }, [submitted, docuSignUrl, countdown]);

  const handleThemeChange = (themeName: string) => {
    setCurrentTheme(themeName);
  };

  useEffect(() => {
    if (slug) {
      loadSurvey();
    }
  }, [slug]);

  useEffect(() => {
    if (survey) {
      // Set browser title to survey name
      document.title = survey.title;
    } else {
      document.title = 'Survey Taker';
    }
  }, [survey]);

  // Normalize survey data to handle both old and new formats
  const normalizeSurveyData = (surveyData: any): Survey => {
    return {
      ...surveyData,
      questions: surveyData.questions?.map((q: any) => {
        // Handle title/questionText compatibility
        const title = q.title || q.questionText || '';
        
        // Handle options format - convert old string[] format to QuestionOption[]
        let normalizedOptions = q.options;
        if (Array.isArray(q.options) && q.options.length > 0) {
          const firstOption = q.options[0];
          // If options are strings, convert to QuestionOption format
          if (typeof firstOption === 'string') {
            normalizedOptions = q.options.map((opt: string, idx: number) => ({
              id: `opt-${q.id}-${idx}`,
              text: opt,
              value: opt
            }));
          }
        }
        
        return {
          ...q,
          title,
          options: normalizedOptions
        };
      }) || []
    };
  };

  const loadSurvey = async () => {
    try {
      console.log('Loading survey with slug:', slug);
      console.log('API_BASE_URL:', API_BASE_URL);
      const url = `${API_BASE_URL}/surveys/by-slug/${slug}`;
      console.log('Fetching from:', url);
      const response = await axios.get(url);
      console.log('Survey loaded successfully:', response.data);
      console.log('Number of questions:', response.data.questions?.length);
      
      // Log questions with options for debugging
      response.data.questions?.forEach((q: any, idx: number) => {
        if (q.options) {
          console.log(`Question ${idx + 1} (${q.type}):`, q.title || q.questionText);
          console.log('  Options:', q.options);
          console.log('  First option structure:', q.options[0]);
        }
      });
      
      // Normalize the data to handle both old and new formats
      const normalizedSurvey = normalizeSurveyData(response.data);
      console.log('Normalized survey:', normalizedSurvey);
      
      setSurvey(normalizedSurvey);
      setLoading(false);
    } catch (err: any) {
      console.error('Failed to load survey:', err);
      console.error('Error response:', err.response?.data);
      setError(err.response?.data?.message || 'Failed to load survey');
      // Set title even for expired/unavailable surveys
      if (err.response?.data?.survey_title) {
        document.title = err.response.data.survey_title;
      }
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId: string, value: any, question?: Question) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
    
    // Check if this question has a conditional follow-up
    if (question?.followUp) {
      const followUpId = `${questionId}_followup`;
      
      // Extract option text for comparison (handle nested structures)
      let selectedValue = value;
      if (typeof value === 'object' && value?.text) {
        selectedValue = typeof value.text === 'object' ? value.text.text : value.text;
      }
      
      console.log('🔔 Follow-up check:', {
        questionTitle: question.title,
        selectedValue,
        triggerOption: question.followUp.triggerOption,
        matches: selectedValue === question.followUp.triggerOption
      });
      
      // Show follow-up if the trigger option is selected
      if (selectedValue === question.followUp.triggerOption) {
        console.log('✅ Showing follow-up:', followUpId);
        setVisibleFollowUps(prev => new Set(prev).add(followUpId));
      } else {
        console.log('❌ Hiding follow-up:', followUpId);
        // Hide follow-up and clear its answer
        setVisibleFollowUps(prev => {
          const newSet = new Set(prev);
          newSet.delete(followUpId);
          return newSet;
        });
        setAnswers(prev => {
          const newAnswers = { ...prev };
          delete newAnswers[followUpId];
          return newAnswers;
        });
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!survey) return;

    // Validate required questions
    const missingRequired = survey.questions
      .filter(q => q.required && !answers[q.id])
      .map(q => q.title);

    if (missingRequired.length > 0) {
      alert(`Please answer all required questions:\n${missingRequired.join('\n')}`);
      return;
    }

    try {
      const answerValues: AnswerValue[] = Object.entries(answers).map(([question_id, value]) => ({
        question_id,
        value
      }));

      console.log('Submitting response:', { survey_id: survey.id, answers: answerValues });

      // Get campaign tracking parameters from URL hash (not query string)
      // Azure Static Web Apps strips query params during routing, so we use hash instead
      const hash = window.location.hash.substring(1); // Remove leading #
      const hashParams = new URLSearchParams(hash);
      console.log('DEBUG: Full URL:', window.location.href);
      console.log('DEBUG: Hash:', window.location.hash);
      console.log('DEBUG: Hash params keys:', Array.from(hashParams.keys()));
      
      let campaignId = hashParams.get('campaign_id') || hashParams.get('cid');
      let recipientId = hashParams.get('recipient_id') || hashParams.get('rid');
      
      // Fallback to sessionStorage if parameters not in hash
      if (!campaignId) {
        campaignId = sessionStorage.getItem('sims_campaign_id');
        recipientId = sessionStorage.getItem('sims_recipient_id');
        if (campaignId) {
          console.log('DEBUG: Retrieved from sessionStorage - campaignId:', campaignId, 'recipientId:', recipientId);
        }
      }
      
      console.log('DEBUG: Extracted campaignId:', campaignId);
      console.log('DEBUG: Extracted recipientId:', recipientId);
      
      // Use the public survey slug-based submit endpoint  
      const submitUrl = `${API_BASE_URL}/public/surveys/${survey.unique_url_slug || survey.slug}/submit`;
      
      // Prepare request body with campaign tracking if present
      const requestBody: any = { answers: answerValues };
      if (campaignId) {
        requestBody.campaign_id = campaignId;
        requestBody.recipient_id = recipientId;
        console.log('Submitting with campaign tracking:', { campaignId, recipientId });
      } else {
        console.warn('DEBUG: No campaign_id found - submitting as anonymous');
      }
      
      console.log('DEBUG: Final request body:', JSON.stringify(requestBody, null, 2));

      const response = await axios.post(submitUrl, requestBody);
      const responseId = response.data.responseId || response.data.id;
      const docusignSent = response.data.docusign_sent || false;

      // Check if this is a graduate placement survey with DocuSign sent via email
      const surveySlug = survey.unique_url_slug || survey.slug;
      const isGraduatePlacement = surveySlug === 'graduate-placement' || 
                                   surveySlug === 'graduate-placement-survey' ||
                                   survey.id === 'graduate-placement-survey';

      if (isGraduatePlacement && docusignSent) {
        console.log('Graduate placement survey - DocuSign sent via email');
        // Set flag to show email delivery message
        setDocuSignUrl('EMAIL_DELIVERY');
      }
      
      setSubmitted(true);
    } catch (err: any) {
      console.error('Submit error:', err.response?.data || err.message);
      const errorMsg = err.response?.data?.message || err.message || 'Failed to submit response';
      alert(`Failed to submit response: ${errorMsg}`);
    }
  };

  const renderQuestion = (question: Question, questionNumber: number) => {
    const value = answers[question.id] || '';

    switch (question.type) {
      case 'multiple-choice':  // Backend format
      case 'multiple_choice':
      case 'radio':
        return (
          <div key={question.id} style={styles.questionCard}>
            <label style={styles.questionTitle}>
              <span style={styles.questionNumber}>{questionNumber}.</span> {question.title}
              {question.required && <span style={styles.required}> *</span>}
            </label>
            <div style={styles.optionsContainer}>
              {question.options?.map((option, idx) => {
                // Debug logging
                if (idx === 0) {
                  console.log('🔍 Processing option for question:', question.title);
                  console.log('  Option type:', typeof option);
                  console.log('  Option value:', option);
                  if (typeof option === 'object' && option?.text) {
                    console.log('  option.text type:', typeof option.text);
                    console.log('  option.text value:', option.text);
                  }
                }
                
                // Ensure we extract text property, handle nested objects and strings
                let optionText: string;
                if (typeof option === 'string') {
                  optionText = option;
                } else if (option && typeof option === 'object') {
                  if (option.text && typeof option.text === 'object' && 'text' in option.text) {
                    // Nested structure: option.text.text
                    optionText = option.text.text;
                  } else if (typeof option.text === 'string') {
                    // Simple structure: option.text
                    optionText = option.text;
                  } else {
                    // Fallback: stringify the option
                    optionText = JSON.stringify(option);
                  }
                } else {
                  optionText = String(option);
                }
                
                const optionId = typeof option === 'object' && option?.id ? option.id : `opt-${idx}-${optionText}`;
                
                console.log('  Final optionText:', optionText);
                
                return (
                  <label key={optionId} style={styles.radioLabel}>
                    <input
                      type="radio"
                      name={question.id}
                      value={optionText}
                      checked={value === optionText}
                      onChange={(e) => {
                        console.log('📻 Radio onChange:', {
                          questionId: question.id,
                          questionTitle: question.title,
                          hasFollowUp: !!question.followUp,
                          followUp: question.followUp,
                          selectedValue: e.target.value
                        });
                        handleAnswerChange(question.id, e.target.value, question);
                      }}
                      style={styles.radioInput}
                    />
                    <span style={styles.radioText}>{optionText}</span>
                  </label>
                );
              })}
            </div>
          </div>
        );

      case 'textarea':
        return (
          <div key={question.id} style={styles.questionCard}>
            <label style={styles.questionTitle}>
              <span style={styles.questionNumber}>{questionNumber}.</span> {question.title}
              {question.required && <span style={styles.required}> *</span>}
            </label>
            <textarea
              value={value}
              onChange={(e) => handleAnswerChange(question.id, e.target.value)}
              placeholder={question.placeholder}
              style={styles.textarea}
              rows={4}
            />
          </div>
        );

      case 'text':
      case 'email':
      case 'number':
      case 'date':
      case 'year':
        return (
          <div key={question.id} style={styles.questionCard}>
            <label style={styles.questionTitle}>
              <span style={styles.questionNumber}>{questionNumber}.</span> {question.title}
              {question.required && <span style={styles.required}> *</span>}
            </label>
            <input
              type={question.type === 'email' ? 'email' : question.type === 'number' ? 'number' : question.type === 'date' ? 'date' : question.type === 'year' ? 'number' : 'text'}
              value={value}
              onChange={(e) => handleAnswerChange(question.id, e.target.value)}
              placeholder={question.placeholder || (question.type === 'year' ? 'YYYY' : '')}
              style={styles.textInput}
              min={question.type === 'year' ? '1900' : undefined}
              max={question.type === 'year' ? '2100' : undefined}
            />
          </div>
        );

      case 'dropdown':  // Backend format
      case 'select':
        return (
          <div key={question.id} style={styles.questionCard}>
            <label style={styles.questionTitle}>
              <span style={styles.questionNumber}>{questionNumber}.</span> {question.title}
              {question.required && <span style={styles.required}> *</span>}
            </label>
            <select
              value={value}
              onChange={(e) => handleAnswerChange(question.id, e.target.value)}
              style={styles.select}
            >
              <option value="">-- Select an option --</option>
              {question.options?.map((option, idx) => {
                // Extract option text safely
                let optionText: string;
                if (typeof option === 'string') {
                  optionText = option;
                } else if (option && typeof option === 'object') {
                  if (option.text && typeof option.text === 'object' && 'text' in option.text) {
                    optionText = option.text.text;
                  } else if (typeof option.text === 'string') {
                    optionText = option.text;
                  } else {
                    optionText = JSON.stringify(option);
                  }
                } else {
                  optionText = String(option);
                }
                
                const optionId = typeof option === 'object' && option?.id ? option.id : `opt-${idx}-${optionText}`;
                
                return (
                  <option key={optionId} value={optionText}>
                    {optionText}
                  </option>
                );
              })}
            </select>
          </div>
        );

      case 'rating':
        return (
          <div key={question.id} style={styles.questionCard}>
            <label style={styles.questionTitle}>
              <span style={styles.questionNumber}>{questionNumber}.</span> {question.title}
              {question.required && <span style={styles.required}> *</span>}
            </label>
            <div style={styles.ratingContainer}>
              {[1, 2, 3, 4, 5].map(rating => (
                <button
                  key={rating}
                  type="button"
                  onClick={() => handleAnswerChange(question.id, rating.toString())}
                  style={{
                    ...styles.ratingButton,
                    ...(value === rating.toString() ? styles.ratingButtonSelected : {})
                  }}
                >
                  {rating}
                </button>
              ))}
            </div>
          </div>
        );

      case 'likert':
        const likertOptions = question.options || [
          { id: '1', text: 'Strongly Disagree', value: '1' },
          { id: '2', text: 'Disagree', value: '2' },
          { id: '3', text: 'Neutral', value: '3' },
          { id: '4', text: 'Agree', value: '4' },
          { id: '5', text: 'Strongly Agree', value: '5' }
        ];
        return (
          <div key={question.id} style={styles.questionCard}>
            <label style={styles.questionTitle}>
              <span style={styles.questionNumber}>{questionNumber}.</span> {question.title}
              {question.required && <span style={styles.required}> *</span>}
            </label>
            <div style={styles.optionsContainer}>
              {likertOptions.map((option, idx) => {
                // Extract option text safely
                let optionText: string;
                if (typeof option === 'string') {
                  optionText = option;
                } else if (option && typeof option === 'object') {
                  if (option.text && typeof option.text === 'object' && 'text' in option.text) {
                    optionText = option.text.text;
                  } else if (typeof option.text === 'string') {
                    optionText = option.text;
                  } else {
                    optionText = JSON.stringify(option);
                  }
                } else {
                  optionText = String(option);
                }
                
                const optionId = typeof option === 'object' && option?.id ? option.id : `opt-${idx}-${optionText}`;
                
                return (
                  <label key={optionId} style={styles.radioLabel}>
                    <input
                      type="radio"
                      name={question.id}
                      value={optionText}
                      checked={value === optionText}
                      onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                      style={styles.radioInput}
                    />
                    <span style={styles.radioText}>{optionText}</span>
                  </label>
                );
              })}
            </div>
          </div>
        );

      case 'textblock':
        // Informational text only, no input
        return (
          <div key={question.id} style={styles.questionCard}>
            <div style={styles.textblockTitle}>
              <span style={styles.questionNumber}>{questionNumber}.</span> {question.title}
            </div>
          </div>
        );

      default:
        // Fallback for unknown types - render as text input
        console.warn('Unknown question type:', question.type, 'rendering as text input');
        return (
          <div key={question.id} style={styles.questionCard}>
            <label style={styles.questionTitle}>
              <span style={styles.questionNumber}>{questionNumber}.</span> {question.title}
              {question.required && <span style={styles.required}> *</span>}
            </label>
            <input
              type="text"
              value={value}
              onChange={(e) => handleAnswerChange(question.id, e.target.value)}
              placeholder={question.placeholder}
              style={styles.textInput}
            />
          </div>
        );
    }
  };

  if (!slug) {
    return (
      <>
        <ThemeSelector currentTheme={currentTheme} onThemeChange={handleThemeChange} />
        <SurveyList />
      </>
    );
  }

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner}></div>
          <p style={styles.loadingText}>Loading survey...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.errorContainer}>
          <h1 style={styles.errorTitle}>Survey Not Found</h1>
          <p style={styles.errorText}>{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    const isWithdrawalSurvey = survey?.slug === 'wait-before-you-go-withdrawal';
    const isGraduatePlacement = docuSignUrl === 'EMAIL_DELIVERY';

    return (
      <div style={styles.container}>
        <div style={styles.successContainer}>
          <div style={styles.successIcon}>✓</div>
          <h1 style={styles.successTitle}>Thank You!</h1>
          
          {isWithdrawalSurvey && (
            <div style={styles.messageBox}>
              <p style={styles.successMessage}>
                Your response helps us keep accurate records and protect your future educational opportunities. 
                If your reason meets certain federal criteria (like military duty or health issues), we can 
                exclude you from graduation rate calculations which keeps our reports fair and helps future students.
              </p>
            </div>
          )}
          
          {isGraduatePlacement && (
            <div style={styles.messageBox}>
              <p style={styles.successMessage}>
                Your response has been recorded successfully.
              </p>
              <div style={{ ...styles.messageBox, backgroundColor: 'var(--info-bg)', borderLeft: '4px solid var(--info-color)', marginTop: '20px' }}>
                <p style={{ margin: '0 0 10px 0', fontWeight: 600, color: 'var(--info-color)' }}>📧 Next Step: Check Your Email</p>
                <p style={{ margin: 0, lineHeight: 1.6 }}>
                  You will receive an email from <strong>DocuSign</strong> within the next few minutes with a link to sign your verification release form. 
                  Please check your inbox (and spam folder) and complete the signature to finalize your graduate placement reporting.
                </p>
              </div>
            </div>
          )}
          
          {!isWithdrawalSurvey && !isGraduatePlacement && (
            <p style={styles.successMessage}>
              Your response has been recorded successfully.
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Global Mobile Preview - Available on all pages */}
      <MobilePreview url={window.location.href} title="SIMS Public Survey Preview" />
      
      <ThemeSelector currentTheme={currentTheme} onThemeChange={handleThemeChange} />
      
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
        <button 
          onClick={() => navigate('/')} 
          style={styles.backButton}
          onMouseEnter={(e) => e.currentTarget.style.background = 'var(--accent-hover)'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'var(--accent)'}
        >
          ← Back to Survey List
        </button>
        
        <button 
          onClick={() => window.location.href = 'https://surveys.lauruscollege.edu/'}
          style={styles.backButton}
          onMouseEnter={(e) => e.currentTarget.style.background = 'var(--accent-hover)'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'var(--accent)'}
        >
          ← Back to Public Surveys
        </button>
      </div>
      
      <div style={styles.header}>
        <div style={styles.logoContainer}>
          <img 
            src="/images/channels4_profile.jpg" 
            alt="Laurus College"
            style={styles.logo}
          />
        </div>
        <h1 style={styles.headerTitle}>Laurus College Survey</h1>
      </div>

      <div style={styles.surveyContainer}>
        <div style={styles.surveyHeader}>
          <h2 style={styles.surveyTitle}>{survey?.title}</h2>
          {survey?.description && (
            <p style={styles.surveyDescription}>{survey.description}</p>
          )}
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          {survey?.questions
            .sort((a, b) => a.order - b.order)
            .map((question, index) => {
              // Group questions by section (using description field)
              // Description format: "Section Title" or "Section Title|||Section Description"
              const currentSectionData = question.description || '';
              const [currentSection, currentSectionDesc] = currentSectionData.split('|||');
              
              const prevQuestion = index > 0 ? survey.questions.sort((a, b) => a.order - b.order)[index - 1] : null;
              const prevSectionData = prevQuestion?.description || '';
              const [prevSection] = prevSectionData.split('|||');
              
              const showSectionHeader = currentSection && currentSection !== prevSection;
              
              return (
                <div key={question.id}>
                  {showSectionHeader && (
                    <div style={styles.sectionHeader}>
                      <h3 style={styles.sectionTitle}>{currentSection}</h3>
                      {currentSectionDesc && (
                        <p style={styles.sectionDescription}>{currentSectionDesc}</p>
                      )}
                    </div>
                  )}
                  {renderQuestion(question, index + 1)}
                  {/* Conditional Follow-up Question (Section-aware) */}
                  {question.followUp && visibleFollowUps.has(`${question.id}_followup`) && (
                    <div style={{ ...styles.questionCard, marginTop: '1rem', marginLeft: '2rem', borderLeft: '3px solid var(--accent)' }}>
                      <label style={styles.questionTitle}>
                        {question.followUp.questionText}
                        {question.required && <span style={styles.required}> *</span>}
                      </label>
                      {question.followUp.type === 'textarea' ? (
                        <textarea
                          value={answers[`${question.id}_followup`] || ''}
                          onChange={(e) => handleAnswerChange(`${question.id}_followup`, e.target.value)}
                          placeholder="Enter your response..."
                          style={styles.textarea}
                          rows={4}
                        />
                      ) : (
                        <input
                          type="text"
                          value={answers[`${question.id}_followup`] || ''}
                          onChange={(e) => handleAnswerChange(`${question.id}_followup`, e.target.value)}
                          placeholder="Enter your response..."
                          style={styles.textInput}
                        />
                      )}
                    </div>
                  )}
                </div>
              );
            })}

          {survey?.closing_message && (
            <div style={styles.closingMessage}>
              <p style={{ whiteSpace: 'pre-line', margin: 0 }}>
                {survey.closing_message}
              </p>
            </div>
          )}

          <button type="submit" style={styles.submitButton}>
            Submit Response
          </button>
        </form>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, var(--bg-gradient-start) 0%, var(--bg-gradient-end) 100%)',
    padding: '0',
    margin: '0',
  },
  backButton: {
    position: 'fixed' as const,
    top: '80px',
    left: '20px',
    padding: '12px 24px',
    background: 'var(--accent)',
    color: 'var(--button-text)',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
    transition: 'all 0.3s ease',
    zIndex: 999,
  },
  header: {
    background: 'var(--bg-secondary)',
    borderBottom: '2px solid var(--accent)',
    padding: '24px 0',
    textAlign: 'center' as const,
  },
  logoContainer: {
    display: 'inline-block',
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    overflow: 'hidden',
    border: '3px solid var(--accent)',
    marginBottom: '12px',
  },
  logo: {
    width: '100%',
    height: '100%',
    objectFit: 'cover' as const,
  },
  headerTitle: {
    color: 'var(--accent)',
    fontSize: '28px',
    fontWeight: '700',
    margin: '0',
  },
  surveyContainer: {
    maxWidth: '800px',
    margin: '40px auto',
    padding: '0 20px',
  },
  surveyHeader: {
    background: 'var(--bg-secondary)',
    padding: '32px',
    borderRadius: '16px',
    marginBottom: '32px',
    border: '1px solid var(--border)',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
  },
  surveyTitle: {
    color: 'var(--accent)',
    fontSize: '32px',
    fontWeight: '700',
    margin: '0 0 16px 0',
  },
  surveyDescription: {
    color: 'var(--text-secondary)',
    fontSize: '16px',
    lineHeight: '1.6',
    margin: '0',
    whiteSpace: 'pre-line' as const,
  },
  form: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '24px',
  },
  questionCard: {
    background: 'var(--bg-secondary)',
    padding: '28px',
    borderRadius: '12px',
    border: '1px solid var(--border)',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
  },
  sectionHeader: {
    background: 'var(--accent)',
    padding: '16px 24px',
    borderRadius: '8px',
    marginTop: '32px',
    marginBottom: '16px',
  },
  sectionTitle: {
    color: 'white',
    fontSize: '20px',
    fontWeight: '700',
    margin: '0',
    marginBottom: '8px',
  },
  sectionDescription: {
    color: 'rgba(255, 255, 255, 0.9)',
    fontSize: '15px',
    lineHeight: '1.5',
    margin: '0',
    marginLeft: '32px',
    fontStyle: 'italic',
  },
  questionNumber: {
    color: 'var(--accent)',
    fontSize: '18px',
    fontWeight: '700',
    marginRight: '8px',
  },
  questionTitle: {
    color: 'var(--accent)',
    fontSize: '18px',
    fontWeight: '600',
    display: 'block',
    marginBottom: '12px',
  },
  questionDescription: {
    color: 'var(--text-secondary)',
    fontSize: '14px',
    marginBottom: '16px',
    fontStyle: 'italic',
  },
  required: {
    color: 'var(--error)',
  },
  optionsContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '12px',
  },
  radioLabel: {
    display: 'flex',
    alignItems: 'flex-start',
    cursor: 'pointer',
    padding: '12px',
    borderRadius: '8px',
    background: 'var(--accent-light)',
    border: '1px solid var(--border)',
    transition: 'all 0.2s ease',
  },
  radioInput: {
    marginRight: '12px',
    marginTop: '3px',
    flexShrink: 0,
    cursor: 'pointer',
    accentColor: 'var(--accent)',
  },
  radioText: {
    color: 'var(--text-secondary)',
    fontSize: '15px',
    lineHeight: '1.5',
  },
  textInput: {
    width: '100%',
    padding: '14px',
    borderRadius: '8px',
    border: '1px solid var(--input-border)',
    background: 'var(--input-bg)',
    color: 'var(--text-primary)',
    fontSize: '15px',
    fontFamily: 'inherit',
  },
  textarea: {
    width: '100%',
    padding: '14px',
    fontSize: '15px',
    border: '2px solid var(--border)',
    borderRadius: '8px',
    background: 'var(--bg-primary)',
    color: 'var(--text-primary)',
    fontFamily: 'inherit',
    resize: 'vertical' as const,
    minHeight: '120px',
  },
  select: {
    width: '100%',
    padding: '14px',
    fontSize: '15px',
    border: '2px solid var(--border)',
    borderRadius: '8px',
    background: 'var(--bg-primary)',
    color: 'var(--text-primary)',
    cursor: 'pointer',
  },
  ratingContainer: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap' as const,
  },
  ratingButton: {
    padding: '12px 20px',
    fontSize: '16px',
    fontWeight: '600',
    border: '2px solid var(--border)',
    borderRadius: '8px',
    background: 'var(--bg-primary)',
    color: 'var(--text-primary)',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    minWidth: '50px',
  },
  ratingButtonSelected: {
    background: 'var(--accent)',
    color: 'white',
    borderColor: 'var(--accent)',
  },
  textblockTitle: {
    color: 'var(--accent)',
    fontSize: '18px',
    fontWeight: '600',
    marginBottom: '12px',
  },
  textblockDescription: {
    color: 'var(--text-primary)',
    fontSize: '15px',
    lineHeight: '1.6',
  },
  closingMessage: {
    background: 'var(--accent)',
    padding: '24px',
    borderRadius: '12px',
    border: '1px solid var(--border)',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
    marginTop: '8px',
    marginBottom: '16px',
    color: 'var(--button-text)',
    fontSize: '16px',
    fontWeight: 'bold',
    lineHeight: '1.6',
  },
  followUpContainer: {
    marginLeft: '32px',
    marginTop: '16px',
    padding: '16px',
    background: 'rgba(124, 58, 237, 0.1)',
    borderRadius: '8px',
    borderLeft: '3px solid var(--accent)',
  },
  followUpLabel: {
    color: 'var(--text-secondary)',
    fontSize: '15px',
    fontWeight: '500',
    display: 'block',
    marginBottom: '12px',
  },
  submitButton: {
    background: 'var(--button-bg)',
    color: 'var(--button-text)',
    padding: '16px 48px',
    fontSize: '18px',
    fontWeight: '700',
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer',
    marginTop: '16px',
    alignSelf: 'center',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
    transition: 'all 0.3s ease',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    gap: '20px',
  },
  spinner: {
    width: '50px',
    height: '50px',
    border: '4px solid var(--accent-light)',
    borderTop: '4px solid var(--accent)',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    color: 'var(--accent)',
    fontSize: '18px',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    padding: '20px',
  },
  errorTitle: {
    color: 'var(--accent)',
    fontSize: '36px',
    marginBottom: '16px',
  },
  errorText: {
    color: 'var(--text-secondary)',
    fontSize: '18px',
  },
  successContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    padding: '40px 20px',
  },
  successIcon: {
    width: '100px',
    height: '100px',
    borderRadius: '50%',
    background: 'var(--button-bg)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '60px',
    color: 'var(--button-text)',
    fontWeight: 'bold',
    marginBottom: '24px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
  },
  successTitle: {
    color: 'var(--accent)',
    fontSize: '42px',
    marginBottom: '24px',
    fontWeight: '700',
  },
  messageBox: {
    maxWidth: '600px',
    background: 'var(--bg-secondary)',
    padding: '32px',
    borderRadius: '16px',
    border: '1px solid var(--border)',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
  },
  successMessage: {
    color: 'var(--text-secondary)',
    fontSize: '18px',
    lineHeight: '1.7',
    textAlign: 'center' as const,
    margin: '0',
  },
  redirectMessage: {
    color: 'var(--accent)',
    fontSize: '20px',
    fontWeight: '600',
    lineHeight: '1.7',
    textAlign: 'center' as const,
    margin: '24px 0 0 0',
  },
  docuSignButton: {
    background: 'var(--accent)',
    color: 'var(--button-text)',
    border: 'none',
    borderRadius: '8px',
    padding: '16px 32px',
    fontSize: '18px',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '24px',
    width: '100%',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
    transition: 'all 0.3s ease',
  },
};

export default App;
